import { db } from '@/lib/firebase';
import { collection, updateDoc, query, where, getDocs, doc, serverTimestamp } from 'firebase/firestore';
import { candidateSchema, type Candidate, normalizeEmail } from '@/lib/schema';

export const getCandidateByEmail = async (email: string) => {
  const normalizedEmail = normalizeEmail(email);
  const q = query(
    collection(db, 'candidates'), 
    where('email', '==', normalizedEmail)
  );
  
  const querySnapshot = await getDocs(q);
  
  if (querySnapshot.empty) {
    return null;
  }
  
  // Get the first matching document
  const candidateDoc = querySnapshot.docs[0];
  const data = candidateDoc.data();
  
  return candidateSchema.parse({
    id: candidateDoc.id,
    ...data,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    anonymizedProfiles: data.anonymizedProfiles?.map((profile: any) => ({
      ...profile,
      processedAt: profile.processedAt?.toDate()
    })) || [],
    resumes: data.resumes?.map((resume: any) => ({
      ...resume,
      uploadedAt: resume.uploadedAt?.toDate()
    })) || []
  }) as Candidate;
};


export const updateCandidate = async (email: string, updates: Partial<Candidate>) => {
  const normalizedEmail = normalizeEmail(email);
  
  // First get the candidate to find the document ID
  const candidate = await getCandidateByEmail(normalizedEmail);
  if (!candidate) {
    throw new Error('Candidate not found');
  }
  
  // Update using the actual document ID (candidate.id is the UUID document ID)
  await updateDoc(doc(db, 'candidates', candidate.id), { 
    ...updates, 
    updatedAt: serverTimestamp() 
  });
  
  return getCandidateByEmail(normalizedEmail);
};

export const incrementReferralCount = async (email: string) => {
  const normalizedEmail = normalizeEmail(email);
  const candidate = await getCandidateByEmail(normalizedEmail);
  if (!candidate) return null;
  
  return updateCandidate(normalizedEmail, {
    referralCount: candidate.referralCount + 1
  });
};

export const addResumeToCandidate = async (email: string, resumeUrl: string, resumeFileName: string) => {
  const normalizedEmail = normalizeEmail(email);
  const candidate = await getCandidateByEmail(normalizedEmail);
  if (!candidate) return null;
  
  return updateCandidate(normalizedEmail, {
    resumeUrl,
    resumeFileName
  });
};

/**
 * Add a resume to the candidate's resumes array
 * This appends to the array instead of replacing the single resume fields
 */
export const addResumeToResumesArray = async (
  email: string, 
  resumeUrl: string, 
  resumeFileName: string,
  uploadedBy: string
) => {
  const normalizedEmail = normalizeEmail(email);
  const candidate = await getCandidateByEmail(normalizedEmail);
  if (!candidate) {
    throw new Error('Candidate not found');
  }
  
  // Get existing resumes or initialize empty array
  const existingResumes = candidate.resumes || [];
  
  // Create new resume entry
  const newResume = {
    url: resumeUrl,
    fileName: resumeFileName,
    uploadedAt: new Date(),
    uploadedBy: uploadedBy
  };
  
  // Add new resume to the array
  const updatedResumes = [...existingResumes, newResume];
  
  // Update candidate with new resumes array and also update single resume fields
  return updateCandidate(normalizedEmail, {
    resumes: updatedResumes,
    resumeUrl: resumeUrl, // Also update the single resume URL field
    resumeFileName: resumeFileName
  });
};

// Search candidates by skills, name, etc.
export const searchCandidates = async (searchTerm?: string, skills?: string[]) => {
  let q = query(collection(db, 'candidates'));
  
  // For now, we'll get all candidates and filter client-side
  // In production, consider using Algolia or similar for better search
  const snapshot = await getDocs(q);
  
  let candidates = snapshot.docs.map(d => {
    const data = d.data();
    return candidateSchema.parse({
      id: d.id,
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      anonymizedProfiles: data.anonymizedProfiles?.map((profile: any) => ({
        ...profile,
        processedAt: profile.processedAt?.toDate()
      })) || [],
      resumes: data.resumes?.map((resume: any) => ({
        ...resume,
        uploadedAt: resume.uploadedAt?.toDate()
      })) || []
    });
  }) as Candidate[];
  
  // Client-side filtering
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    candidates = candidates.filter(candidate => 
      candidate.firstName.toLowerCase().includes(searchLower) ||
      candidate.lastName.toLowerCase().includes(searchLower) ||
      candidate.email.toLowerCase().includes(searchLower)
    );
  }
  
  return candidates;
};

export const getAllCandidates = async () => {
  return searchCandidates();
};

// Get candidate by spannerUserId (direct field or from anonymizedProfiles)
export const getCandidateBySpannerId = async (spannerId: string): Promise<Candidate | null> => {
  try {    
    // Get all candidates and search through their spannerUserId field and anonymizedProfiles
    const allCandidates = await searchCandidates();
    
    for (const candidate of allCandidates) {
      // First check if spannerUserId is a direct field on the candidate
      if (candidate.spannerUserId === spannerId) {
        return candidate;
      }
      
      // Also check in anonymizedProfiles for backwards compatibility
      if (candidate.anonymizedProfiles) {
        for (const profile of candidate.anonymizedProfiles) {
          if (profile.spannerUserId === spannerId) {
            return candidate;
          }
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('[ERROR] Error searching for candidate by spannerId:', error);
    return null;
  }
};
